import { useEffect, useRef, useState } from 'react';
import type { ShellEvent } from '@momomail/shared';
import { Controller, type RawWorld } from '../../../mascot/app/renderer/controller.js';
import { cellOf, rigFrom } from '../../../mascot/app/renderer/rig.js';
import { shell } from '../lib/bridge';
import { ASSISTANT_OPEN, ASSISTANT_STATE, type CatAnchor } from './Assistant';
import { bedAnchor, CAT_HOME, CAT_PICKUP, overBed } from './CatBed';

/** The cat's standing height in the window (px; on the desktop it is 120 — it shrinks on the leap in, see LeapStep). */
const H = 64;
/** The desktop app serves the cat's sheets here (apps/desktop/scripts/build.mjs). */
const ASSETS = 'desktop-cat/assets/';
const CLICK_MOVE = 6;
const CLICK_MS = 300;
const KEEP_MS = 20_000;
/** How near the cat's pixels the mouse counts as on it (px). */
const SLACK = 4;

/** Whether the left button is down (a pick-up out of the bed starts before this page runs the cat: its let-go may come first). */
let buttonDown = false;
if (typeof window !== 'undefined') {
  window.addEventListener('mousedown', (e) => e.button === 0 && (buttonDown = true), true);
  window.addEventListener('mouseup', (e) => e.button === 0 && (buttonDown = false), true);
  // A mouseup this page never got (the window lost the mouse mid-drag: Alt-Tab, a dialog, another window on top): the next move shows
  // the button up, and losing focus counts as letting go — or the cat would keep following a pointer that let it go long ago
  window.addEventListener('mousemove', (e) => (e.buttons & 1) === 0 && (buttonDown = false), true);
  window.addEventListener('blur', () => (buttonDown = false));
}

interface Clip {
  name: string;
  sheet: string;
  cell: { w: number; h: number; cols: number };
  origin: { x: number; y: number };
  small?: { sheet: string; w: number; h: number; scale: number };
  k: number;
}

/**
 * What the cat can stand on in this window, in page px: the mail list's rows (their top edges, while in view), its bed, the list pane
 * (its sides are walls to climb), and the window's bottom as the floor. Rows go in front of the pane.
 */
function readWorld(): RawWorld {
  const W = window.innerWidth;
  const Hh = window.innerHeight;
  const rect = (el: Element | null) => {
    const r = el?.getBoundingClientRect();
    return r && r.width > 0 && r.height > 0 ? { x: r.left, y: r.top, w: r.width, h: r.height } : null;
  };
  const windows: RawWorld['windows'] = [];
  const bed = rect(document.querySelector('.cat-bed'));
  if (bed) windows.push({ id: 'bed', rect: bed });
  const scroller = rect(document.querySelector('.list__scroll'));
  if (scroller)
    for (const el of document.querySelectorAll<HTMLElement>('.list__scroll .row[data-id]')) {
      const r = rect(el);
      // Only rows whose top edge is in view (one scrolled up under the list's header is no ledge)
      if (r && r.y >= scroller.y && r.y < scroller.y + scroller.h - 4) windows.push({ id: `row${el.dataset.id}`, rect: { ...r, h: Math.min(r.h, scroller.y + scroller.h - r.y) } });
    }
  const list = rect(document.querySelector('.pane.list'));
  if (list) windows.push({ id: 'list', rect: list });
  return { displays: [{ id: 'window', x: 0, y: 0, w: W, h: Hh, floor: Hh }], windows };
}

/** Where a cat leaping in from the desktop lands: on a row in the middle of the list, else on the floor below it. */
function landingSpot(): { x: number; y: number } {
  const world = readWorld();
  const rows = world.windows.filter((w) => w.id.startsWith('row'));
  const row = rows[Math.min(rows.length - 1, 2)];
  if (row) return { x: row.rect.x + row.rect.w * 0.5, y: row.rect.y };
  const list = world.windows.find((w) => w.id === 'list');
  return { x: list ? list.rect.x + list.rect.w * 0.5 : window.innerWidth * 0.45, y: window.innerHeight };
}

/**
 * The desktop cat inside the app's window (desktop app only): it leaps in from the desktop (cat-enter), lives here — walking the
 * mail list's rows, its bed and the window's floor with the desktop cat's own brain (apps/mascot) — and goes back out when the
 * window goes (cat-leave). A click opens 馍馍's chat; holding picks it up, and let go over its bed it goes in and stays (CatReport
 * home) — or out of the window, it drops at the edge. Picked up out of the bed it is here, held (CatReport out). While it is out on
 * the desktop, the page tells the desktop where a leap can land.
 */
export function WindowCat({ enabled, out, assets = ASSETS }: { enabled: boolean; out: boolean; assets?: string }) {
  const [entry, setEntry] = useState<{ x: number; y: number; pose: string; dir: number; held?: { x: number; y: number } } | null>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const report = shell?.cat?.report;

  // The desktop says when it comes and goes
  useEffect(() => {
    if (!shell?.cat) return;
    return shell.onEvent((e: ShellEvent) => {
      if (e.type === 'cat-enter') setEntry({ x: e.x, y: e.y, pose: e.pose, dir: e.dir });
      else if (e.type === 'cat-leave') setEntry(null);
    });
  }, []);
  useEffect(() => {
    if (!enabled) setEntry(null);
  }, [enabled]);

  // Picked up out of its bed: here, hanging from the mouse (from the bed's anchor)
  useEffect(() => {
    if (!enabled || !report) return;
    const pick = (e: Event) => {
      const at = bedAnchor();
      const { x, y } = (e as CustomEvent<{ x: number; y: number }>).detail;
      if (!at) return;
      report({ type: 'out', x: at.x, y: at.y });
      setEntry({ x: at.x, y: at.y, pose: 'stand_stare', dir: 1, held: { x, y } });
    };
    window.addEventListener(CAT_PICKUP, pick);
    return () => window.removeEventListener(CAT_PICKUP, pick);
  }, [enabled, report]);

  // While it is out on the desktop: where it could land
  useEffect(() => {
    if (!enabled || !out || entry || !report) return;
    const send = () => document.visibilityState === 'visible' && report({ type: 'spot', ...landingSpot() });
    send();
    const timer = setInterval(send, 1000);
    return () => clearInterval(timer);
  }, [enabled, out, entry, report]);

  // While it is here: run it
  useEffect(() => {
    if (!entry || !report) return;
    let stop = false;
    let raf = 0;
    const cleanups: (() => void)[] = [];
    void fetch(`${assets}mascot.json`)
      .then((r) => r.json())
      .then((manifest: { catHeight: number; clips: Clip[] }) => {
        const el = canvas.current;
        if (stop || !el) return;
        const g = el.getContext('2d')!;
        const probe = Object.assign(document.createElement('canvas'), { width: 1, height: 1 }).getContext('2d', { willReadFrequently: true })!;
        let dpr = window.devicePixelRatio || 1;
        const size = () => {
          dpr = window.devicePixelRatio || 1;
          el.width = Math.round(window.innerWidth * dpr);
          el.height = Math.round(window.innerHeight * dpr);
        };
        size();
        window.addEventListener('resize', size);
        cleanups.push(() => window.removeEventListener('resize', size));

        // Sheets as drawn here: half-size ones are plenty for a 64 px cat on most screens
        const s = H / manifest.catHeight;
        const small = dpr <= 2;
        const clips = new Map(
          manifest.clips.map((c) => [
            c.name,
            small && c.small
              ? { ...c, sheet: c.small.sheet, cell: { w: c.small.w, h: c.small.h, cols: c.cell.cols }, origin: { x: c.origin.x * c.small.scale, y: c.origin.y * c.small.scale }, k: s / c.small.scale }
              : { ...c, k: s },
          ]),
        );
        const images = new Map<string, { img: HTMLImageElement; used: number }>();
        const sheet = (c: Clip) => {
          let entry = images.get(c.name);
          if (!entry) images.set(c.name, (entry = { img: Object.assign(new Image(), { src: `${assets}${c.sheet}` }), used: 0 }));
          entry.used = performance.now();
          return entry.img.complete && entry.img.naturalWidth ? entry.img : null;
        };

        const ctl = new Controller(rigFrom(manifest, H), H, readWorld());
        ctl.placeAt(entry.x, entry.y, entry.pose, entry.dir);

        // The world follows the page: scrolling, resizing, rows coming and going
        let worldKey = '';
        const refresh = () => {
          const raw = readWorld();
          const key = JSON.stringify(raw);
          if (key !== worldKey) (worldKey = key), ctl.setWorld(raw);
        };
        const poll = setInterval(refresh, 120);
        const scroller = document.querySelector('.list__scroll');
        scroller?.addEventListener('scroll', refresh, { passive: true });
        cleanups.push(() => (clearInterval(poll), scroller?.removeEventListener('scroll', refresh)));

        // Where it is, for the desktop to put it back there when the window goes
        const tell = setInterval(() => report({ type: 'state', x: ctl.cat.x, y: ctl.cat.y, dir: ctl.cat.dir }), 200);
        cleanups.push(() => clearInterval(tell));
        // Let unused sheets go
        const sweep = setInterval(() => {
          const now = performance.now();
          const wanted = new Set([ctl.view().clip, ...ctl.upcoming()]);
          for (const [name, e] of images) if (!wanted.has(name) && now - e.used > KEEP_MS) (e.img.src = ''), images.delete(name);
        }, 5000);
        cleanups.push(() => clearInterval(sweep));

        // The mouse: over the cat's own pixels (or within a few px of them: it is small and moving, and a press just off its legs
        // would pick up the mail row under it instead)
        let shown: { c: Clip; img: HTMLImageElement; frame: number; x: number; y: number; dir: number } | null = null;
        const hit = (px: number, py: number) => {
          const f = shown;
          if (!f) return false;
          const r = Math.ceil(SLACK / f.c.k);
          const lx = Math.floor(((px - f.x) * f.dir) / f.c.k + f.c.origin.x);
          const ly = Math.floor((py - f.y) / f.c.k + f.c.origin.y);
          const x0 = Math.max(0, lx - r);
          const y0 = Math.max(0, ly - r);
          const x1 = Math.min(f.c.cell.w, lx + r + 1);
          const y1 = Math.min(f.c.cell.h, ly + r + 1);
          if (x1 <= x0 || y1 <= y0) return false;
          const { sx, sy } = cellOf(f.c, f.frame);
          const w = x1 - x0;
          const h = y1 - y0;
          if (probe.canvas.width < w || probe.canvas.height < h) Object.assign(probe.canvas, { width: Math.max(w, probe.canvas.width), height: Math.max(h, probe.canvas.height) });
          probe.clearRect(0, 0, w, h);
          probe.drawImage(f.img, sx + x0, sy + y0, w, h, 0, 0, w, h);
          const px4 = probe.getImageData(0, 0, w, h).data;
          for (let i = 3; i < px4.length; i += 4) if (px4[i]! > 40) return true;
          return false;
        };
        /** The held cat over its bed: the mouse over it, or the cat hanging from the mouse (its body below the hand) over it. */
        const intoBed = () => overBed(pointer.x, pointer.y) || overBed(pointer.x, pointer.y + H * 0.6);
        let pressed: { x: number; y: number; t: number } | null = null;
        let grabbed = false;
        let over = false;
        /** The press was the cat's: the click that follows it isn't the page's (wherever it lands). */
        let swallow = false;
        /** Where the mouse is (in the page: inside, null when it left). */
        const pointer = { x: 0, y: 0 };
        let inside = false;
        /** The hand when over the cat: on mouse moves, and as the cat moves under a still mouse. */
        const hover = () => {
          const now = inside && !grabbed && hit(pointer.x, pointer.y);
          if (now !== over) root.toggle('cat-hover', (over = now));
        };
        const root = document.documentElement.classList;
        const bedTarget = (on: boolean) => document.querySelector('.cat-bed')?.classList.toggle('is-target', on);
        const grab = (x: number, y: number) => {
          ctl.grab(x, y, performance.now());
          grabbed = true;
          root.remove('cat-hover');
          root.add('cat-grabbing');
        };
        /** The mouse let go (or left the window): into the bed if it is over it, else it drops from there. */
        const letGo = () => {
          const wasGrabbed = grabbed;
          pressed = null;
          grabbed = false;
          root.remove('cat-grabbing');
          bedTarget(false);
          if (!wasGrabbed) return;
          if (intoBed()) {
            // Home: the bed has it now (and the desktop cat goes off); it stays there until picked up again
            stop = true;
            report({ type: 'home' });
            window.dispatchEvent(new Event(CAT_HOME));
            setEntry(null);
          } else ctl.release();
        };
        const move = (e: MouseEvent) => {
          pointer.x = e.clientX;
          pointer.y = e.clientY;
          if (grabbed) {
            // Out of the window with it: it slips from the hand at the edge and drops
            const W = window.innerWidth;
            const Hh = window.innerHeight;
            if (e.clientX < 0 || e.clientY < 0 || e.clientX >= W || e.clientY >= Hh) {
              pointer.x = Math.min(W - 1, Math.max(0, e.clientX));
              pointer.y = Math.min(Hh - 1, Math.max(0, e.clientY));
              ctl.movePointer(pointer.x, pointer.y, performance.now());
              return letGo();
            }
            bedTarget(intoBed());
            return;
          }
          inside = true;
          hover();
          if (pressed && (Math.hypot(e.clientX - pressed.x, e.clientY - pressed.y) > CLICK_MOVE || performance.now() - pressed.t > CLICK_MS)) grab(pressed.x, pressed.y);
        };
        const leave = () => {
          inside = false;
          hover();
          if (grabbed) letGo();
        };
        // Picked up out of the bed: it is held already
        if (entry.held) {
          pointer.x = entry.held.x;
          pointer.y = entry.held.y;
          pressed = { ...entry.held, t: performance.now() };
          swallow = true;
          grab(entry.held.x, entry.held.y);
        }
        const down = (e: MouseEvent) => {
          if (e.button !== 0 || !hit(e.clientX, e.clientY)) return;
          // The cat is on top of whatever is under it: the press is the cat's
          e.preventDefault();
          e.stopPropagation();
          pressed = { x: e.clientX, y: e.clientY, t: performance.now() };
          swallow = true;
        };
        // (nor a drag of the mail row under it: that would carry the row off instead of the cat)
        const dragStart = (e: DragEvent) => {
          if (pressed || grabbed) e.preventDefault();
        };
        const up = (e: MouseEvent) => {
          if (!pressed) return;
          e.preventDefault();
          e.stopPropagation();
          // A click: its chat, by it (and it stays there meanwhile)
          if (!grabbed) window.dispatchEvent(new CustomEvent<CatAnchor>(ASSISTANT_OPEN, { detail: { x: ctl.cat.x, top: ctl.cat.y - H, bottom: ctl.cat.y } }));
          letGo();
        };
        // (and the click that follows the press, so the row under the cat isn't opened)
        const click = (e: MouseEvent) => {
          if (!swallow) return;
          swallow = false;
          e.preventDefault();
          e.stopPropagation();
        };
        // (a press anywhere else: the page's again — the cat's own presses stop at the window, before this)
        const downAnywhere = () => void (swallow = false);
        const staying = (e: Event) => void (ctl.staying = !!(e as CustomEvent<boolean>).detail);
        window.addEventListener('mousemove', move, true);
        window.addEventListener('mousedown', down, true);
        window.addEventListener('mouseup', up, true);
        window.addEventListener('click', click, true);
        window.addEventListener('mousedown', downAnywhere);
        window.addEventListener('dragstart', dragStart, true);
        window.addEventListener(ASSISTANT_STATE, staying);
        document.documentElement.addEventListener('mouseleave', leave);
        cleanups.push(() => {
          document.documentElement.removeEventListener('mouseleave', leave);
          root.remove('cat-grabbing');
          bedTarget(false);
          window.removeEventListener('mousemove', move, true);
          window.removeEventListener('mousedown', down, true);
          window.removeEventListener('mouseup', up, true);
          window.removeEventListener('click', click, true);
          window.removeEventListener('mousedown', downAnywhere);
          window.removeEventListener('dragstart', dragStart, true);
          window.removeEventListener(ASSISTANT_STATE, staying);
          document.documentElement.classList.remove('cat-hover');
        });

        let last = performance.now();
        let announced = false;
        const frame = (now: number) => {
          if (stop) return;
          if (pressed && !grabbed && now - pressed.t > CLICK_MS) grab(pressed.x, pressed.y);
          // (the button came up where this page did not see it)
          if (grabbed && !buttonDown) letGo();
          if (stop) return;
          if (grabbed) ctl.movePointer(pointer.x, pointer.y, now);
          else hover();
          ctl.tick(Math.min(0.05, (now - last) / 1000));
          last = now;
          for (const name of ctl.upcoming()) {
            const c = clips.get(name);
            if (c) sheet(c);
          }
          const v = ctl.view();
          const c = v.clip ? clips.get(v.clip) : undefined;
          const img = c && sheet(c);
          if (c && img) shown = { c, img, frame: v.frame, x: v.x, y: v.y, dir: v.dir };
          else if (shown) Object.assign(shown, { x: v.x, y: v.y, dir: v.dir });
          g.setTransform(dpr, 0, 0, dpr, 0, 0);
          g.clearRect(0, 0, window.innerWidth, window.innerHeight);
          if (shown) {
            const { sx, sy, w, h } = cellOf(shown.c, shown.frame);
            g.save();
            g.translate(shown.x, shown.y);
            g.scale(shown.dir, 1);
            g.drawImage(shown.img, sx, sy, w, h, -shown.c.origin.x * shown.c.k, -shown.c.origin.y * shown.c.k, w * shown.c.k, h * shown.c.k);
            g.restore();
            // It is drawn here now: the desktop can stop drawing it
            if (!announced) (announced = true), report({ type: 'ready' });
          }
          raf = requestAnimationFrame(frame);
        };
        raf = requestAnimationFrame(frame);
      })
      .catch(() => {});
    return () => {
      stop = true;
      cancelAnimationFrame(raf);
      for (const f of cleanups) f();
      canvas.current?.getContext('2d')?.clearRect(0, 0, canvas.current.width, canvas.current.height);
    };
  }, [entry, report, assets]);

  if (!report || !enabled) return null;
  return <canvas ref={canvas} className="window-cat" aria-hidden />;
}
